import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import { Label as LabelIcon } from '@mui/icons-material';
import { conversationsAPI, labelsAPI } from '../../services/api';

interface Label {
  id: number;
  title: string;
  color: string;
  description?: string;
}

interface LabelsDialogProps {
  open: boolean;
  onClose: () => void;
  conversationId: number;
  currentLabels: Label[];
  onLabelsUpdated: () => void;
}

const LabelsDialog: React.FC<LabelsDialogProps> = ({
  open,
  onClose,
  conversationId,
  currentLabels,
  onLabelsUpdated
}) => {
  const [availableLabels, setAvailableLabels] = useState<Label[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (open) {
      loadAvailableLabels();
      setSelectedLabels(currentLabels.map(label => label.id));
    }
  }, [open, currentLabels]);

  const loadAvailableLabels = async () => {
    try {
      setLoading(true);
      const response = await labelsAPI.getLabels();
      // API returns array directly, not wrapped in an object
      setAvailableLabels(Array.isArray(response) ? response : (response.labels || []));
    } catch (error) {
      console.error('Failed to load labels:', error);
      setAvailableLabels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLabelToggle = (labelId: number) => {
    setSelectedLabels(prev => 
      prev.includes(labelId)
        ? prev.filter(id => id !== labelId)
        : [...prev, labelId]
    );
  };

  const handleUpdateLabels = async () => {
    try {
      setUpdating(true);
      await conversationsAPI.updateLabels(conversationId, selectedLabels);
      onLabelsUpdated();
      onClose();
    } catch (error) {
      console.error('Failed to update labels:', error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: '85vh' } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LabelIcon />
          Manage Labels
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select labels for this conversation:
            </Typography>
            
            {availableLabels.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No labels available. Create labels first to organize conversations.
              </Typography>
            ) : (
              <Box>
                {availableLabels.map((label) => (
                  <Box key={label.id} sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedLabels.includes(label.id)}
                          onChange={() => handleLabelToggle(label.id)}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            size="small"
                            label={label.title}
                            sx={{ 
                              bgcolor: label.color,
                              color: 'white',
                              '& .MuiChip-label': { px: 1 }
                            }}
                          />
                          {label.description && (
                            <Typography variant="caption" color="text.secondary">
                              {label.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={updating}>
          Cancel
        </Button>
        <Button 
          onClick={handleUpdateLabels} 
          variant="contained"
          disabled={updating || loading}
        >
          {updating ? 'Updating...' : 'Update Labels'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LabelsDialog;